import { ExportingOptions } from "highcharts";
import Enumerable from "linq";
import { IListTextTagMapping_V2, ILookupListText_V2 } from "../../common/models/ILookupList";
import { ChartIdentifier, ElementWithCharts, GraphItemOptions, IGraphSeriesOptions, ITrendDataViewModel } from "../../services/trending-data-service/Models";
import UUID from "./Uuid";
import ThemeSet from "../../themes/Default";
import { createTheme } from "@mui/material";
import { themifyHighchartsOptions } from "./ThemifyHighchartsOptions";
import Highcharts from "highcharts";

const GraphUtils = {

    addLegendItemClickEvent(chartInstances: Map<ChartIdentifier, Highcharts.Chart>, hiddenSeries: Map<number, Array<string>>,
        chartOptions: Highcharts.Options, chartIndex: number, synchronizeLegends: boolean) {

        if (chartOptions.plotOptions) {

            if (chartOptions.plotOptions.series) {

                if (chartOptions.plotOptions.series.events) {

                    chartOptions.plotOptions.series.events.legendItemClick = function () {
                        GraphUtils.onClickLegendItem(chartInstances, hiddenSeries, chartIndex, this.name, synchronizeLegends, !this.visible);
                        return true;
                    }
                } else {

                    chartOptions.plotOptions.series.events = {
                        legendItemClick: function () {
                            GraphUtils.onClickLegendItem(chartInstances, hiddenSeries, chartIndex, this.name, synchronizeLegends, !this.visible);
                            return true;
                        }
                    }
                }
            } else {

                chartOptions.plotOptions.series = {
                    events: {
                        legendItemClick: function () {
                            GraphUtils.onClickLegendItem(chartInstances, hiddenSeries, chartIndex, this.name, synchronizeLegends, !this.visible);
                            return true;
                        }
                    }
                };
            }

        } else {
            chartOptions.plotOptions = {
                series: {
                    events: {
                        legendItemClick: function () {
                            GraphUtils.onClickLegendItem(chartInstances, hiddenSeries, chartIndex, this.name, synchronizeLegends, !this.visible);
                            return true;
                        }
                    }
                }
            };
        }
    },
    //getChartID(index: number) {
    //    return `chart-${index}`;
    //},
    getChartID() {
        // Create a unique chart ID
        return `chart-${UUID.generate()}`;
    },
    getDataCount(data: Array<ITrendDataViewModel> | undefined): number {

        let dataCount: number = 0;

        if (data) {
            Enumerable.from(data)
                .selectMany(tvm => Enumerable.from(tvm?.chartOptions?.series ?? [])
                    .select(s => {
                        dataCount += ((s as any)?.data?.length ?? 0);
                    }))
                .toArray();
        }

        return dataCount;
    },
    // http://api.highcharts.com/highcharts#exporting
    getGraphExportingOptions(name: string | undefined, graphItemOptions: GraphItemOptions): ExportingOptions {
        const theme = createTheme(ThemeSet.light.main);
        const lightThemeOptions = themifyHighchartsOptions(graphItemOptions, theme);

        const chartOptions = {
            chart: {
                ...lightThemeOptions.chart,
                backgroundColor: theme.palette?.background?.default,
                plotBackgroundColor: theme.palette?.background?.default,
            },
            legend: lightThemeOptions.legend,
            xAxis: lightThemeOptions.xAxis,
            yAxis: lightThemeOptions.yAxis,
            subtitle: undefined
        }

        const exportingOptions: ExportingOptions =
        {
            buttons: undefined,
            scale: 1, // Defaults to 2
            sourceHeight: 768,
            sourceWidth: 1024,
            fallbackToExportServer: false,
            filename: name,
            chartOptions: chartOptions
        };

        return exportingOptions;
    },
    getGraphHeight(clientHeight: number | undefined, numCharts: number | undefined, numGraphsInViewport: number): string | undefined {

        let graphHeight: string | undefined = undefined;

        // We only need to set a height if there is more than one chart in the Viewport
        if (clientHeight && numCharts && (numCharts > 1) && (numCharts >= numGraphsInViewport)) {

            // Subtract a little extra for padding.
            const clientHalfHeight = (clientHeight / numGraphsInViewport) - 5;

            graphHeight = `${clientHalfHeight}px`;
        }

        return graphHeight;
    },

    getSeriesTooltipPointFormatter(tagID: number | undefined, listTextTagMapping?: IListTextTagMapping_V2 | undefined) {

        if (tagID !== undefined && tagID > 0 && listTextTagMapping && ((listTextTagMapping?.listIDTagIDs?.length ?? 0) > 0) &&
            ((listTextTagMapping?.listTexts?.length ?? 0) > 0)) {

            // Get the Tag to List ID mapping if there is one.
            const lstID: number | undefined = listTextTagMapping.listIDTagIDs.find(o => o.tagID === tagID)?.lstID;
            const listTextItems: Array<ILookupListText_V2> | undefined = ((lstID ?? 0) > 0)
                ? listTextTagMapping.listTexts.find(lt => lt.id === lstID)?.lt : undefined;

            return ((lstID ?? 0) > 0 && ((listTextItems?.length ?? 0) > 0)) ? function (this: any) {

                const thisPoint = this as any;

                const listTextItem: ILookupListText_V2 | undefined = listTextItems?.find(o => o.nv === thisPoint.y);
                let text: string | undefined = listTextItem ? listTextItem.desc : `${thisPoint.y}`;

                return `<span style="color:${thisPoint.color}">\u25CF</span> ${(this as any).series.name}: <b>${text}</b><br/>`;
            }
                : undefined;

        } else {
            return undefined;
        }
    },

    // In order to synchronize tooltips and crosshairs, override the
    // built-in events with handlers defined on the parent element.
    SyncAddMouseMouseMove(chartInstances: Map<ChartIdentifier, Highcharts.Chart>, 
        element: Element | undefined,
        allowSyncMove: boolean) {

        if (element && (chartInstances?.size ?? 0) > 0) {

            // The event handlers need the chartInstance so add them to the element
            (element as ElementWithCharts).chartInstances = chartInstances;

            // This is a hack to fix a bug in Highcharts version 8.2.2. It may not
            //  be needed in later versions. For some reason with our custom code in
            //  _chartMoveEvent a single click puts Highcharts in drag selection mode.
            //  It seems to be something in the chart mouseOver call.
            //  This hack stops it.
            element.addEventListener("mouseup", GraphUtils.SyncMouseUpEvent);
            element.addEventListener("touchend", GraphUtils.SyncMouseUpEvent);

            if (allowSyncMove) {
                GraphUtils.SyncGetMouseMoveEvents().forEach(et => {
                    element.addEventListener(et, GraphUtils.SyncMouseMoveEvent);
                });
            }

            GraphUtils.SyncGetMouseMoveStopEvents().forEach(et => {

                element.addEventListener(et, GraphUtils.SyncMouseMoveStopEvent);
            });
        }
    },
    SyncGetMouseMoveEvents(): Array<string> {
        return ["mousemove", "touchmove", "touchstart"];
    },
    SyncGetMouseMoveStopEvents(): Array<string> {
        return ["mouseleave", "touchend"];
    },
    SyncMouseMoveEvent(event: Event) {

        //event.stopPropagation();

        // The event handlers need the chartInstance so add them to the element
        const chartInstances: Map<ChartIdentifier, Highcharts.Chart> = (event.currentTarget as ElementWithCharts).chartInstances ??
            new Map<ChartIdentifier, Highcharts.Chart>();

        chartInstances.forEach(chartInstance => {

            const chart: any = chartInstance;

            let points: Array<any> = [];
            const visibleSeries: any = Enumerable.from(chart.series)
                .where(series => (series as any).visible === true)
                .toArray();

            if (chart && chart.pointer) {

                points = [];

                // Find coordinates within the chart
                event = chart.pointer.normalize(event);

                const hoverPoint = chart.pointer.findNearestKDPoint(visibleSeries, chart.tooltip.shared, event);

                if (hoverPoint) {

                    // Get all points with the same x value as the hoverPoint
                    visibleSeries.forEach(function (series: { points: any[]; }) {

                        series.points.forEach(point => {

                            if (point.x === hoverPoint.x && !point.isNull) {

                                points.push(point);

                                // Show the hover marker
                                //This causes safari scrolling issue
                                //Bug 49689: React Mobile - Unable to Scroll - Group Graph
                                point.onMouseOver(event);
                            }
                        });
                    });

                    // Show the crosshair
                    chart.xAxis[0].drawCrosshair(event, hoverPoint);
                }

                // if (hoverPoint) {
                //     // Show the crosshair
                //     chart.xAxis[0].drawCrosshair(event, hoverPoint);
                // }

                if (Array.isArray(visibleSeries) && points.length > 0) {

                    if (Array.isArray(points) && points.length > 0) {
                        // Show the tooltip for the visible points
                        //This causes safari scrolling issue
                        //Bug 49689: React Mobile - Unable to Scroll - Group Graph                        
                        chart.tooltip.refresh(chart.tooltip.shared ? points : hoverPoint);
                    }
                }
                // If there are no visible Series, hide everything
                else {
                    chart.xAxis[0].hideCrosshair();

                    if (Array.isArray(points) && points.length > 0) {
                        // hide the tooltip
                        chart.tooltip.hide(points);
                    }
                }
            }
        });
    },
    SyncMouseMoveStopEvent(event: Event) {

        //event.stopPropagation();
        //event.preventDefault();
        
        // The event handlers need the chartInstance so add them to the element
        const chartInstances: Map<ChartIdentifier, Highcharts.Chart> = (event.currentTarget as ElementWithCharts).chartInstances ??
            new Map<ChartIdentifier, Highcharts.Chart>();

        chartInstances.forEach(chartInstance => {

            const chart: any = chartInstance;

            let points: Array<any> = [];

            if (chart && chart.pointer) {

                points = [];

                // Find coordinates within the chart
                event = chart.pointer.normalize(event);

                const hoverPoint = chart.pointer.findNearestKDPoint(chart.series, true, event);

                if (hoverPoint) {

                    // Get all points with the same x value as the hoverPoint
                    chart.series.forEach(function (series: { points: any[]; }) {

                        series.points.forEach(point => {

                            if (point.x === hoverPoint.x && !point.isNull) {

                                points.push(point);

                                // Hide the hover marker
                                point.onMouseOut();
                            }
                        });
                    });
                }

                chart.xAxis[0].hideCrosshair();

                if (Array.isArray(points) && points.length > 0) {
                    // hide the tooltip

                    console.log('hiding tooltip')
                    chart.tooltip.hide(points);
                }
            }
        });
    },
    // This is a hack to fix a bug in Highcharts version 8.2.2. It may not
    //  be needed in later versions. For some reason with our custom code in
    //  _chartMoveEvent a single click puts Highcharts in drag selection mode.
    //  It seems to be something in the chart mouseOver call.
    //  This hack stops it.
    SyncMouseUpEvent(event: Event) {

        // The event handlers need the chartInstance so add them to the element
        const chartInstances: Map<ChartIdentifier, Highcharts.Chart> = (event.currentTarget as ElementWithCharts).chartInstances ??
            new Map<ChartIdentifier, Highcharts.Chart>();

        chartInstances.forEach(chartInstance => {

            const chart: any = chartInstance;
            chart.mouseIsDown = undefined;
        });
    },
    SyncRemoveMouseMove(element: Element | undefined) {

        if (element) {

            element.removeEventListener("mouseup", GraphUtils.SyncMouseUpEvent);
            element.removeEventListener("touchend", GraphUtils.SyncMouseUpEvent);

            GraphUtils.SyncGetMouseMoveEvents().forEach(et => {
                element.removeEventListener(et, GraphUtils.SyncMouseMoveEvent);
            });

            GraphUtils.SyncGetMouseMoveStopEvents().forEach(et => {
                element.removeEventListener(et, GraphUtils.SyncMouseMoveStopEvent);
            });
        }
    },
    hasData(data: Array<ITrendDataViewModel> | undefined, requiredCount: number): boolean {

        let dataCount: number = GraphUtils.getDataCount(data);
        return dataCount > requiredCount;
    },
    onClickLegendItem(chartInstances: Map<ChartIdentifier, Highcharts.Chart>, hiddenSeries: Map<number, Array<string>>, chartIndex: number,
        seriesName: string | undefined, synchronizeLegends: boolean, visible: boolean) {

        if (synchronizeLegends === true) {

            chartInstances.forEach((chartInstance, chartIdentifier) => {

                if (chartIdentifier.index !== chartIndex) {

                    chartInstance.series.forEach((curSeries) => {

                        if (curSeries.name === seriesName) {

                            (curSeries as any).update({
                                visible: visible
                            }, true);
                        }
                    });
                }
            });
        }

        // If the Legends are synchronized, we only need to use the first hidden series list.
        const curChartIndex: number = !synchronizeLegends ?
            chartIndex : 0;

        let hiddenSeriesItem: Array<string> | undefined = hiddenSeries.get(curChartIndex);

        if (!Array.isArray(hiddenSeriesItem)) {
            hiddenSeriesItem = [];
            hiddenSeries.set(curChartIndex, hiddenSeriesItem);
        }

        if (visible && visible === true) {

            if ((hiddenSeriesItem as any).includes(seriesName)) {
                hiddenSeries.set(curChartIndex, hiddenSeriesItem.filter(item => item !== seriesName));
            }
        } else {
            if (seriesName && (seriesName?.length ?? 0) > 0 &&
                !(hiddenSeriesItem as any).includes(seriesName)) {

                hiddenSeriesItem.push(seriesName);
            }
        }
    },
    resetScrollPosition(element: HTMLElement | undefined) {

        if (element) {
            element.scrollTop = 0;
        }
    },
    resizeWithTimer(chartInstances: Map<ChartIdentifier, Highcharts.Chart>, showAxes: boolean) {

        let timerID: number | undefined;
        const clearTimer = () => {
            if (timerID) {
                clearTimeout(timerID);
                timerID = undefined;
            }
        };

        const startTimer = () => {

            clearTimer();

            timerID = window.setTimeout(() => {

                ((chartInstances.size ?? 0) > 0) &&
                    chartInstances.forEach(chartInstance => {

                        if (!showAxes) {
                            chartInstance.redraw();
                        } else {
                            chartInstance.reflow();
                        }
                    });

                clearTimer();

            }, 250);
        };

        if ((chartInstances.size ?? 0) > 0) {
            startTimer();
        }

        return () => {
            clearTimer();
        }
    },
    saveGraphImage(chart: Highcharts.Chart) {

        chart && (chart as any).exportChartLocal();
    },

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Adapted from:
    // https://jsfiddle.net/gh/get/jquery/1.7.2/highcharts/highcharts/tree/master/samples/highcharts/exporting/multiple-charts-offline/
    saveGraphImages(
        charts: Array<Highcharts.Chart>,
        options: ExportingOptions
    ) {
        const combinedOptions = Highcharts.merge(
            Highcharts.getOptions().exporting,
            options
        );

        // Get SVG asynchronously and then download the resulting SVG
        getSVG(
            charts,
            combinedOptions,
            (svg: string) => Highcharts.downloadSVGLocal(
                svg,
                combinedOptions,
                () => console.log('Failed to export on client side')
            )
        );
    },
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    setListTextTooltip(chartOptions: Highcharts.Options, listTextTagMapping: IListTextTagMapping_V2 | undefined): void {

        // Only do this if we have a tag to list text mapping
        if (chartOptions.series && listTextTagMapping) {

            chartOptions.series.forEach(series => {

                const graphSeries: IGraphSeriesOptions = series as IGraphSeriesOptions;

                if (!graphSeries.tooltip) {

                    graphSeries.tooltip = {};
                }

                if (!(graphSeries.tooltip as any).pointFormatter) {

                    // Get the Tag to List ID mapping if there is one.
                    const lstID: number | undefined = listTextTagMapping.listIDTagIDs.find(o => o.tagID === graphSeries.tagID)?.lstID;

                    if ((lstID ?? 0) > 0) {

                        (graphSeries as any).tooltip = {
                            ...graphSeries.tooltip,
                            pointFormatter: GraphUtils.getSeriesTooltipPointFormatter(graphSeries.tagID, listTextTagMapping)
                        }
                    }
                }
            });
        }
    },
    setSeriesVisible(chartOptions: Highcharts.Options, hiddenSeriesItem: Array<string> | undefined): void {

        if (chartOptions.series) {

            if ((hiddenSeriesItem?.length ?? 0) > 0) {

                chartOptions.series.forEach(series => {
                    if ((hiddenSeriesItem as any).includes(series.name)) {
                        series.visible = false;
                    }
                });
            } else {

                chartOptions.series.forEach(series => {
                    series.visible = true;
                });
            }
        }
    },
    setSynchronizedChartOptions(chartInstances: Map<ChartIdentifier, Highcharts.Chart>, chartOptions: Highcharts.Options): void {

        const xAxis: Highcharts.XAxisOptions[] = chartOptions.xAxis as Highcharts.XAxisOptions[];

        if ((xAxis?.length ?? 0) > 0) {

            //(data.xAxis[0] as any).crosshair = true;

            (xAxis[0] as any).crosshair = {
                color: 'black',
                width: 1,
                snap: false,
                zIndex: 7
            }

            //if (chartOptions.chart && chartOptions.chart.zoomType) {
            if (chartOptions.chart && chartOptions.chart.zooming?.type) {

                // I am not sure why this was in the SCADALynx version. Without the 
                //  "Click and drag in the plot area to zoom in" text in the React App the Zoom Reset button
                //  is cut off at the top

                //chartOptions.chart.resetZoomButton = {
                //    position: {
                //        // align: 'right', // by default
                //        // verticalAlign: 'top', // by default
                //        x: 0,
                //        y: -30
                //    }
                //}

                if (!xAxis[0].events) {
                    xAxis[0].events =
                    {
                        setExtremes: undefined
                    }
                }

                xAxis[0].events.setExtremes = (event) => {

                    //console.log("***setExtremes");

                    // Prevent feedback loop for syncExtremes
                    if ((event as any).trigger !== 'syncExtremes') {

                        const thisChart = (this as any).chart;
                        const target: any = event.target;

                        let minDiff: number | undefined = undefined;
                        let maxDiff: number | undefined = undefined;

                        if (target && target.chart && Array.isArray(target.chart.xAxis) && event.min && event.max) {

                            minDiff = event.min - target.chart.xAxis[0].min;
                            maxDiff = target.chart.xAxis[0].max - event.max;
                        }

                        chartInstances.forEach(chartInstance => {

                            //console.log("***setExtremes chart iteration");

                            const chart: any = chartInstance;

                            // Prevent feedback loop for the same chart
                            if (chart !== thisChart) {

                                // Prevent feedback loop for hasZoomedOut
                                if (!chart.hasZoomedOut) {

                                    const xAxis: any = chart.xAxis[0];

                                    // It is undefined while updating
                                    if (xAxis.setExtremes) {

                                        let min: number = event.min;
                                        let max: number = event.max;

                                        if (minDiff !== undefined && maxDiff !== undefined) {

                                            min = chart.xAxis[0].min + minDiff;
                                            max = chart.xAxis[0].max - maxDiff;
                                        }

                                        xAxis.setExtremes(min, max, undefined, false, {
                                            trigger: 'syncExtremes'
                                        });

                                        const resetZoomButton = chart.resetZoomButton;

                                        if (min && max) {

                                            if (!resetZoomButton) {

                                                chart.showResetZoom();

                                                if (chart.resetZoomButton) {

                                                    chart.resetZoomButton.element.style.zIndex = 999999;
                                                }
                                            }

                                        } else if (typeof resetZoomButton === 'object') {

                                            chart.resetZoomButton = resetZoomButton.destroy();
                                        }
                                    }
                                } else {
                                    chart.hasZoomedOut = undefined;
                                }

                                //chart.reflow();
                            }
                        });
                    }
                }
            }
        }
    },
    updateAxesVisibility(chart: Highcharts.Chart, visible: boolean) {

        
        if (chart.yAxis && chart.yAxis.length > 1) {

            chart.yAxis.forEach((axis, index) => {

                if (axis.options.opposite === true) {

                    axis.update({
                        visible: visible
                    }, false);
                }
            });
        }
    },
    updateAllAxesVisibility(charts: Array<Highcharts.Chart>, visible: boolean) {

        charts && charts.forEach(chart => GraphUtils.updateAxesVisibility(chart, visible));
    }
};

function getSVG(
    charts: Array<Highcharts.Chart>,
    options: ExportingOptions,
    callback: (svg: string) => void
) {
    let top = 0;
    let width = 0;
    const svgArr: Array<any> = [];

    function addSVG(svgres: string) {
        // Grab width/height from exported chart
        const svgWidth = +(svgres as any).match(/^<svg[^>]*width\s*=\s*"?(\d+)"?[^>]*>/)[1];
        const svgHeight = +(svgres as any).match(/^<svg[^>]*height\s*=\s*"?(\d+)"?[^>]*>/)[1];

        // Offset the position of this chart in the final SVG
        let svg = svgres.replace(
            '<svg',
            `<g transform="translate(0,${top})" `
        );
        svg = svg.replace('</svg>', '</g>');
        top += svgHeight;
        width = Math.max(width, svgWidth);

        svgArr.push(svg);
    }

    function exportChart(i: number) {
        if (i === charts.length) {
            return callback(
                `<svg 
                    version="1.1" 
                    width="${width}" 
                    height="${top}" 
                    viewBox="0 0 ${width} ${top}"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    ${svgArr.join('')}
                </svg>`
            );
        }

        (charts[i] as any).getSVGForLocalExport(
            options, 
            {}, 
            () => {
                console.log('Failed to get SVG');
            },
            (svg: string) => {
                addSVG(svg);
                // Export next only when this SVG is received
                return exportChart(i + 1);
            }
        );
    };

    exportChart(0);
}

export default GraphUtils;
